/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import get from 'lodash/get';
import { gnoment } from 'shared-ui/gnoment';

import { getCurrentDayString } from './index';
import {
    AVAILABILITY_KEY_MAP_FORMAT,
    AVAILABILITY_FILTERS,
    DAY_UNBLOCKED,
    DAY_BLOCKED_DATE_IN_THE_PAST,
    DAY_BLOCKED_OUTSIDE_LOTTERY_SEASON,
    DAY_BLOCKED_BY_LOTTERY,
    DAY_BLOCKED_MISSING_QUOTA,
    DAY_BLOCKED_NO_QUOTA,
} from './constants';

// NOTE: casing - consider revising across repo
export const isDayWalkupOnly = (day, props) => {
    const dayFormat = day.startOf('day').format(AVAILABILITY_KEY_MAP_FORMAT);
    return get(
        props,
        `availability.quotaTypeMaps.constantQuotaUsageByStartDate[${dayFormat}].showWalkup`,
        false
    );
};

export const isDayLotteryOnly = (day, props) => {
    const dayFormat = day.startOf('day').format(AVAILABILITY_KEY_MAP_FORMAT);
    const { divisionId } = props;
    const dayAvailability = get(props, `availability[${divisionId}].date_availability`);
    if (dayAvailability && dayAvailability[dayFormat]) {
        return dayAvailability[dayFormat].show_lottery;
    }

    // TODO: change data format (quick hotfix below) also figure out why quotaType in props is undefined
    const dayAvailabilities = get(
        props,
        `availabilities[${divisionId}].ConstantQuotaUsageByStartDate`
    );
    if (dayAvailabilities && dayAvailabilities[dayFormat]) {
        return dayAvailabilities[dayFormat].show_lottery;
    }
    // END OF TODO

    return false;
};

export const isWalkupQuotaType = (quotaType) => quotaType === 'walkup';

export const isDayManagedByLottery = (day, props) => {
    if (isDayLotteryOnly(day, props)) {
        const { dateMap } = props;
        const dayString = day.format(AVAILABILITY_KEY_MAP_FORMAT);
        if (!dateMap[dayString]) return false;
        return true;
    }
    return false;
};

export const isDayBeingBlockedByLottery = (day, props) =>
    props.blockLotteryDates && isDayManagedByLottery(day, props);

export const isDayBlockedWithReason = (day, props) => {
    const todayString = getCurrentDayString(props);
    const dayString = day.format(AVAILABILITY_KEY_MAP_FORMAT);
    if (props.isBypassQuotaCheck) {
        return DAY_UNBLOCKED;
    }
    if (dayString < todayString) {
        // Day is in the past. If it's a retroactive walkup, allow it. Otherwise, block it.
        if (isWalkupQuotaType(props.quotaType)) {
            return DAY_UNBLOCKED;
        }
        return DAY_BLOCKED_DATE_IN_THE_PAST;
    }
    if (props.blockNonLotteryDates && !isDayManagedByLottery(day, props)) {
        return DAY_BLOCKED_OUTSIDE_LOTTERY_SEASON;
    }
    if (isDayBeingBlockedByLottery(day, props)) {
        return DAY_BLOCKED_BY_LOTTERY;
    }
    if (!props.dateMap) {
        return DAY_BLOCKED_MISSING_QUOTA;
    }
    if (!props.dateMap[day.format(AVAILABILITY_KEY_MAP_FORMAT)]) {
        return DAY_BLOCKED_NO_QUOTA;
    }
    return DAY_UNBLOCKED;
};

export const isDayBlocked = (day, props) =>
    isDayBlockedWithReason(day, props) !== DAY_UNBLOCKED;

export const getFilterErrors = (filterValues, groupSize) => {
    const errors = Object.keys(filterValues)
        .map((quotaFilterType) => {
            if (groupSize === 0) {
                const quotaFilterName = `${quotaFilterType.charAt(0).toUpperCase()}${quotaFilterType.slice(1)}`;
                const filter = AVAILABILITY_FILTERS().find(
                    (availabilityFilter) => quotaFilterName === availabilityFilter.type
                );
                return (
                    get(filter, 'minValue', 1) > groupSize &&
                    get(filter, 'missingInputError')
                );
            }
            return undefined;
        })
        .filter((errorMessage) => !!errorMessage);
    return errors;
};

export const isDaySameAsTodayOnUTC = (date) => {
    const now = gnoment.utc();
    return now.startOf('day').isSame(date, 'day');
};
